/**
 * The joint-operations rollup: every AOR at once, so a commander overseeing
 * several areas does not have to fly into each to know where the trouble is.
 *
 * It buckets each aircraft into its AOR by position and rolls the fleet-health
 * grades up per area with the same rule the single fleet grade uses — the worst
 * aircraft governs the area. An average per area would do here exactly what it
 * would do for one fleet: hide the one aircraft in trouble behind healthy
 * neighbours. So nothing averages, and the area a commander should look at
 * first is the one showing critical, however many green aircraft sit beside it.
 *
 * Aircraft outside every AOR are not dropped — they are their own bucket, so a
 * grounded airframe drifting between areas is never lost from the count.
 *
 * Pure: graded aircraft with positions in, a per-area rollup out.
 */

import { AORS, positionInAor } from '@/map/aors';
import type { HealthGrade } from '@/api/fleetHealth';

export interface GradedAircraft {
  droneId: string;
  grade: HealthGrade;
  lat?: number;
  lon?: number;
}

export interface AorRollup {
  aorId: string;
  total: number;
  counts: Record<HealthGrade, number>;
  /** The worst grade any aircraft in the area holds. Empty area is unknown. */
  worstGrade: HealthGrade;
}

const SEVERITY: Record<HealthGrade, number> = {
  healthy: 0,
  unknown: 1,
  degraded: 2,
  critical: 3,
};

/** The worst of several grades. Matches the fleet-health worst-governs rule. */
export function worstGrade(grades: HealthGrade[]): HealthGrade {
  if (grades.length === 0) return 'unknown';
  return grades.reduce((worst, g) => (SEVERITY[g] > SEVERITY[worst] ? g : worst), 'healthy');
}

function emptyCounts(): Record<HealthGrade, number> {
  return { healthy: 0, degraded: 0, critical: 0, unknown: 0 };
}

/** Special bucket id for aircraft outside every AOR. */
export const UNASSIGNED = 'UNASSIGNED';

export function jocRollup(aircraft: GradedAircraft[]): AorRollup[] {
  const buckets = new Map<string, GradedAircraft[]>();
  for (const aor of AORS) buckets.set(aor.id, []);
  buckets.set(UNASSIGNED, []);

  for (const ac of aircraft) {
    const aor = AORS.find((a) => positionInAor(ac.lat, ac.lon, a));
    buckets.get(aor ? aor.id : UNASSIGNED)!.push(ac);
  }

  const rollups: AorRollup[] = [];
  for (const [aorId, members] of buckets) {
    // An empty AOR is not shown; the UNASSIGNED bucket is shown only when it
    // holds something, so the rollup is areas that actually have aircraft.
    if (members.length === 0) continue;
    const counts = emptyCounts();
    for (const member of members) counts[member.grade] += 1;
    rollups.push({
      aorId,
      total: members.length,
      counts,
      worstGrade: worstGrade(members.map((m) => m.grade)),
    });
  }

  // Worst area first — the one to look at before the calm ones.
  rollups.sort((a, b) => SEVERITY[b.worstGrade] - SEVERITY[a.worstGrade] || b.total - a.total);
  return rollups;
}
